/**
 * Email Events Service
 * 
 * Logs email events to Firestore for analytics and future email sending integration.
 * Events are stored in teams/{teamId}/emailEvents collection.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { EmailContext, EmailProvider } from '@/utils/email';
import type { MessageChannel } from '@/stores/emailCompose.store';
import { logger } from '@/lib/logger';

// =============================================================================
// Types
// =============================================================================

export type EmailEventType = 
  | 'email_opened'      // User opened email in external client
  | 'whatsapp_opened'   // User opened WhatsApp with prefilled message
  | 'email_sent'        // Future: Email sent via API
  | 'email_delivered'   // Future: Email delivery confirmed
  | 'email_failed';     // Future: Email delivery failed

export interface EmailEvent {
  /** Event type */
  type: EmailEventType;
  
  /** Message channel (email or whatsapp) */
  channel: MessageChannel;
  
  /** Email context (customer, workOrder, invoice, appointment) */
  context: EmailContext;
  
  /** Template ID used */
  templateId: string;
  
  /** Email provider used (for email channel) */
  provider?: EmailProvider;
  
  /** Recipient email address (for email) or phone number (for whatsapp) */
  recipient: string;
  
  /** @deprecated Use recipient instead */
  recipientEmail?: string;
  
  /** Email subject (also used as WhatsApp message title) */
  subject: string;
  
  // Optional references
  /** Customer ID if available */
  customerId?: string;
  /** Invoice number if context is invoice */
  invoiceNumber?: string;
  /** Work order ID if context is workOrder */
  workOrderId?: string;
  /** Appointment ID if context is appointment */
  appointmentId?: string;
  
  /** User who triggered the event */
  createdBy: string;
}

// =============================================================================
// Service Functions
// =============================================================================

/**
 * Log an email event to Firestore
 * 
 * @param teamId - Team ID
 * @param event - Email event data
 * @returns Document ID of the created event
 */
export async function logEmailEvent(
  teamId: string,
  event: EmailEvent
): Promise<string | null> {
  try {
    const eventsRef = collection(db, `teams/${teamId}/emailEvents`);
    
    const docRef = await addDoc(eventsRef, {
      ...event,
      createdAt: serverTimestamp(),
    });
    
    logger.log('[EmailEvents] Event logged:', event.type, event.context, docRef.id);
    return docRef.id;
  } catch (error) {
    // Don't throw - logging should not break the main flow
    logger.error('[EmailEvents] Failed to log event:', error);
    return null;
  }
}

/**
 * Log a message opened event (email or WhatsApp)
 * 
 * Convenience wrapper for the most common event types.
 */
export async function logMessageOpened(
  teamId: string,
  options: {
    channel: MessageChannel;
    context: EmailContext;
    templateId: string;
    provider?: EmailProvider;
    recipient: string;
    subject: string;
    customerId?: string;
    invoiceNumber?: string;
    workOrderId?: string;
    appointmentId?: string;
    createdBy: string;
  }
): Promise<string | null> {
  const eventType: EmailEventType = options.channel === 'whatsapp' ? 'whatsapp_opened' : 'email_opened';
  
  return logEmailEvent(teamId, {
    type: eventType,
    ...options,
  });
}

/**
 * @deprecated Use logMessageOpened instead
 * Log an email opened event
 */
export async function logEmailOpened(
  teamId: string,
  options: {
    context: EmailContext;
    templateId: string;
    provider: EmailProvider;
    recipientEmail: string;
    subject: string;
    customerId?: string;
    invoiceNumber?: string;
    workOrderId?: string;
    appointmentId?: string;
    createdBy: string;
  }
): Promise<string | null> {
  return logMessageOpened(teamId, {
    channel: 'email',
    context: options.context,
    templateId: options.templateId,
    provider: options.provider,
    recipient: options.recipientEmail,
    subject: options.subject,
    customerId: options.customerId,
    invoiceNumber: options.invoiceNumber,
    workOrderId: options.workOrderId,
    appointmentId: options.appointmentId,
    createdBy: options.createdBy,
  });
}
